import { useState, useEffect, useCallback, useMemo } from 'react';
import { FormikProps } from 'formik';
import { VisualType } from './components/Switch/Switch';
import { withWindow } from '@shared/utils/withWindow';
import { ContactCollectorInputFields } from '@apps/contactCollector/ContactCollector.controller';
import type { ContactCollectionEventFragment } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { useContactCollectorTelemetry } from '@apps/contactCollector/ContactCollector.telemetry';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useGetGuestSitePages } from '@shared/hooks/useGetGuestSitePages';
import { useEventInfo } from '@shared/utils/eventInfo';

const getRandomInt = (max: number) => Math.floor(Math.random() * max);

export const useEditorController = (
  eventHandle: string,
  eventByName: Maybe<ContactCollectionEventFragment>,
  formik: FormikProps<ContactCollectorInputFields>,
  setOpenDialog?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const { getValueString } = useQueryParamHelper();
  const isUpdating = getValueString('modify') === 'true';
  const { buttonInteracted } = useContactCollectorTelemetry();
  const { tq } = useTranslation('contactCollector');
  const { pages: guestSitePages } = useGetGuestSitePages({ pages: eventByName?.pages });
  const { eventInfo } = useEventInfo();

  const info = eventByName?.info;
  const defaultNames = tq`The Couple`;
  const names = (info?.ownerFirstName && info?.fianceeFirstName ? `${info?.ownerFirstName} & ${info?.fianceeFirstName}` : info?.ownerFirstName) || defaultNames;

  const getPlaceholder = useCallback(
    (index: number): string => {
      switch (index) {
        case 0:
          return tq`Hey! Mind sharing your current mailing address? 📮 ${names} are excited to send you a little something special!`;
        case 1:
          return tq`Hey! 👋 ${names} are gathering addresses to send you something special! Could you share yours?`;
        case 2:
          return tq`Hi! Could you share your mailing address? ${names} have a special surprise to send your way!`;
        case 3:
          return tq`You're invited! But first, we need your mailing address. Click the link to share with ${names}.`;
        case 4:
          return tq`Hey! ${names} are collecting addresses to mail you a special invitation. Can you provide yours? 📬`;
        case 5:
          return tq`Can you provide your mailing address? ${names} want to send something your way!`;
        case 6:
          return tq`Hey! ${names} are getting ready to send you something special. Can you share your address with them?`;
        case 7:
          return tq`${names} are collecting addresses for their event. Please click the link to share.`;
        default:
          return tq`Can you share your current mailing address? ${names} would love to send you something special! 💌`;
      }
    },
    [names, tq],
  );
  const date = info?.dateV0918?.dateString;

  const [selectedVisualType, setSelectedVisualType] = useState<VisualType>(VisualType.ANIMATION);
  const [placeHolder, setPlaceHolder] = useState<string>();

  const { mediaSource, mediaType, message, websiteLinkEnabled, eventDateDisplayEnabled } = formik.values;

  useEffect(() => setSelectedVisualType(mediaType), [mediaType]);

  const handleOnGoToSettingsClick = () => {
    withWindow(() => {
      window.open(`${window.location.origin}/${eventHandle}/edit/settings`, '_blank');
    });
  };

  const handleOnGoToSecuritySettingsClick = () => {
    withWindow(() => {
      window.open(`${window.location.origin}/${eventHandle}/edit/settings/security`, '_blank');
    });
  };

  const getRandomPlaceHolder = useCallback(() => {
    const position = getRandomInt(8);
    return getPlaceholder(position);
  }, [getPlaceholder]);

  const handleOnCreateMagicLinkClick = () => {
    formik.handleSubmit();
    setOpenDialog?.(false);
    buttonInteracted({
      buttonType: 'save',
      element: isUpdating ? 'updateMagicLink' : 'createMagicLink',
      extraInfo: {
        eventHandle,
        visual: selectedVisualType === VisualType.IMAGE ? 'event photo' : 'animation',
        animation: selectedVisualType === VisualType.IMAGE ? 'none' : mediaSource,
        message,
        linkToWebsite: websiteLinkEnabled,
        displayDate: eventDateDisplayEnabled,
        eventDate: date ?? '',
      },
    });
  };

  const handleOnRestartClick = () => {
    const newPlaceHolder = getRandomPlaceHolder();
    setPlaceHolder(newPlaceHolder);
    formik.setFieldValue('message', newPlaceHolder);
  };

  // Translated strings for the Editor UI
  const translations = useMemo(
    () => ({
      visual: tq`Set a Visual`,
      photo: tq`Photo source`,
      note: tq`Write a Brief Note`,
      message: tq`Your Message`,
      messagePlaceholder: tq`Can you share your current mailing address? ${names} would love to send you something special! 💌`,
      linkWebsite: tq`Link to Event Website`,
      displayEventDate: tq`Display Event Date`,
      editDate: tq`Edit Date`,
      privateDate: tq`Your Event Date is currently set to private. If you'd like it displayed here, please update your preferences in`,
      settings: tq`Settings`,
      refresh: tq`, and refresh this page.`,
      updateMagicLink: tq`Update Magic Link`,
      createMagicLink: tq`Create Magic Link`,
      magicLinkError: tq`There was an error creating the Magic Link`,
    }),
    [names, tq],
  );

  return {
    selectedVisualType,
    setSelectedVisualType,
    handleOnGoToSettingsClick,
    handleOnGoToSecuritySettingsClick,
    handleOnCreateMagicLinkClick,
    handleOnRestartClick,
    mediaSource,
    message,
    websiteLinkEnabled,
    eventDateDisplayEnabled,
    pages: guestSitePages,
    privateEventDate: !!eventInfo?.settings.requirePasswordForEventDate,
    date,
    names,
    translations,
    placeHolder,
    isUpdating,
  };
};
